/* =========================================================
   Brand teaser — DermX Tattoo / DermX Beauty

   Sección de tránsito rápido. Tres efectos independientes, sin pin:

   1. Parallax del fondo: NO se implementa aquí. Es el atributo data-speed que
      lee ScrollSmoother a nivel de theme. Este archivo solo decide qué valor
      lleva (escritorio / móvil / movimiento reducido).
   2. Reveal del bloque de texto + CTA: fade-in + slide-up al 80% del viewport,
      una sola vez, sin scrub.
   3. Desenfoque progresivo (opcional, por schema): filter blur -> 0 atado al
      scroll con scrub, en su propio ScrollTrigger. Si el checkbox está
      desactivado no se crea nada.

   Comparte con split-reveal.js la promesa window.dermxGsapReady, así que en
   una home con varias secciones GSAP se descarga una única vez.
   ========================================================= */

(function () {
  'use strict';

  /* En el Theme Editor la sección re-inyecta su <script> y este archivo se
     vuelve a ejecutar. Si ya hay un módulo vivo, reutilizamos el suyo. */
  if (window.DermxBrandTeaser) {
    window.DermxBrandTeaser.initAll();
    return;
  }

  var GSAP_VERSION = '3.13.0';
  var GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/gsap.min.js';
  var SCROLLTRIGGER_URL =
    'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/ScrollTrigger.min.js';

  var DESKTOP = '(min-width: 750px)';
  var MOBILE = '(max-width: 749px)';
  var MOTION_OK = '(prefers-reduced-motion: no-preference)';
  var MOTION_REDUCED = '(prefers-reduced-motion: reduce)';

  /* Una instancia por section.id: { mm } */
  var instances = new Map();

  /* ---------------------------------------------------------
     Carga de GSAP + ScrollTrigger (una sola vez por página)
     --------------------------------------------------------- */

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-gsap-src="' + src + '"]');

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', function () {
            resolve();
          });
          existing.addEventListener('error', reject);
        }
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserva el orden gsap -> ScrollTrigger
      script.dataset.gsapSrc = src;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function isPluginRegistered(name) {
    return !!(window.gsap && window.gsap.core && window.gsap.core.globals()[name]);
  }

  /* Promesa compartida: si otra sección de la home ya pidió GSAP, se reutiliza
     y no se inyecta el script por duplicado. */
  function ensureGsap() {
    if (window.dermxGsapReady) return window.dermxGsapReady;

    window.dermxGsapReady = Promise.resolve()
      .then(function () {
        if (!window.gsap) return loadScript(GSAP_URL);
        return null;
      })
      .then(function () {
        if (!window.gsap) throw new Error('[brand-teaser] GSAP no disponible');
        if (!window.ScrollTrigger && !isPluginRegistered('ScrollTrigger')) {
          return loadScript(SCROLLTRIGGER_URL);
        }
        return null;
      })
      .then(function () {
        var gsap = window.gsap;
        var ScrollTrigger = window.ScrollTrigger || gsap.core.globals().ScrollTrigger;

        if (!ScrollTrigger) throw new Error('[brand-teaser] ScrollTrigger no disponible');
        if (!isPluginRegistered('ScrollTrigger')) gsap.registerPlugin(ScrollTrigger);

        // En móvil, mostrar/ocultar la barra de direcciones cambia innerHeight:
        // sin esto ScrollTrigger recalcularía a media animación y daría saltos.
        ScrollTrigger.config({ ignoreMobileResize: true });

        return { gsap: gsap, ScrollTrigger: ScrollTrigger };
      });

    return window.dermxGsapReady;
  }

  /* ---------------------------------------------------------
     Utilidades
     --------------------------------------------------------- */

  var refreshTimer = null;

  function refreshSoon(ScrollTrigger) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      ScrollTrigger.refresh();
    }, 60);
  }

  /* Las imágenes/vídeos que aún no han cargado cambian el layout: refrescamos. */
  function refreshOnMedia(root, ScrollTrigger) {
    root.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener(
        'load',
        function () {
          refreshSoon(ScrollTrigger);
        },
        { once: true }
      );
    });
  }

  /* El parallax lo aplica ScrollSmoother, no este archivo. Escribimos el
     atributo (para cuando ScrollSmoother arranque después) y, si ya está vivo,
     re-registramos el efecto: ScrollSmoother lee data-speed al crear el efecto,
     no en cada refresh, así que cambiar solo el atributo no bastaría.
     speed === null -> sin parallax (equivale a velocidad 1). */
  function applyParallaxSpeed(media, speed) {
    if (speed === null) {
      media.removeAttribute('data-speed');
    } else {
      media.setAttribute('data-speed', speed);
    }

    var ScrollSmoother =
      window.ScrollSmoother ||
      (window.gsap && window.gsap.core && window.gsap.core.globals().ScrollSmoother);
    var smoother = ScrollSmoother && ScrollSmoother.get && ScrollSmoother.get();

    if (!smoother) return;
    // refresh: false para no forzar un refresh sincrónico por llamada; el
    // debounce de refreshSoon se encarga, y así un resize no encadena varios.
    smoother.effects(media, { speed: speed === null ? 1 : speed, refresh: false });
  }

  /* ---------------------------------------------------------
     Ciclo de vida por sección
     --------------------------------------------------------- */

  function destroy(sectionId) {
    var instance = instances.get(sectionId);
    if (!instance) return;

    if (instance.onRefreshInit) {
      var ScrollTrigger =
        window.ScrollTrigger ||
        (window.gsap && window.gsap.core && window.gsap.core.globals().ScrollTrigger);
      if (ScrollTrigger) ScrollTrigger.removeEventListener('refreshInit', instance.onRefreshInit);
    }

    instance.mm.revert(); // mata tweens, sets y ScrollTriggers de esta sección
    instances.delete(sectionId);
  }

  function init(root) {
    if (!root) return;

    var sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    destroy(sectionId); // evita duplicados al re-renderizar en el editor

    ensureGsap()
      .then(function (lib) {
        // El editor puede haber quitado la sección mientras cargaba el CDN.
        if (!root.isConnected) return;
        build(root, sectionId, lib.gsap, lib.ScrollTrigger);
      })
      .catch(function (error) {
        // Sin GSAP mostramos el estado final: la sección sigue siendo usable.
        root.classList.add('brand-teaser--static');
        console.warn(error);
      });
  }

  function build(root, sectionId, gsap, ScrollTrigger) {
    var media = root.querySelector('[data-brand-teaser-media]');
    var image = root.querySelector('.brand-teaser__image');
    var content = root.querySelector('[data-brand-teaser-content]');

    var desktopSpeed = parseFloat(root.dataset.parallaxSpeed);
    var mobileSpeed = parseFloat(root.dataset.mobileParallaxSpeed);
    var blurEnabled = root.dataset.blurEnabled === 'true';
    var blurAmount = parseFloat(root.dataset.blurAmount) || 0;
    var blurScrollVh = parseFloat(root.dataset.blurScroll) || 50;

    /* Colchón vertical de la capa de fondo. Liquid ya calculó un valor de
       partida asumiendo sección = viewport; aquí se ajusta con las alturas
       reales, que es lo que de verdad determina el recorrido del efecto:
       ScrollSmoother desplaza la capa ±(E + V) * d / 2, con E su propio alto.
       Despejando p en p * S >= desplazamiento, y con E = S * (1 + 2p):

         p = (S + V) * d / (2 * S * (1 - d))

       Con sección a pantalla completa (S = V) se reduce a d / (1 - d), pero en
       cuanto la sección es más baja que el viewport (por ejemplo con un
       max-height) hace falta más colchón, y esto lo recoge. */
    var currentSpeed = desktopSpeed;

    function updateMediaOverflow() {
      if (!media) return;

      var delta = Math.abs(1 - currentSpeed);
      var sectionHeight = root.offsetHeight;

      if (!sectionHeight || !delta) {
        root.style.removeProperty('--brand-teaser-media-overflow');
        root.style.removeProperty('--brand-teaser-media-overflow-mobile');
        return;
      }

      var padding =
        ((sectionHeight + window.innerHeight) * delta) / (2 * sectionHeight * (1 - delta));
      var value = (gsap.utils.clamp(0.04, 1, padding) * 100).toFixed(1) + '%';

      // Las dos variables al mismo valor: currentSpeed ya refleja el breakpoint
      // activo, así que no hay que distinguir cuál lee el CSS.
      root.style.setProperty('--brand-teaser-media-overflow', value);
      root.style.setProperty('--brand-teaser-media-overflow-mobile', value);
    }

    // refreshInit corre antes de que ScrollTrigger mida, que es justo cuando
    // el colchón tiene que estar puesto. Cubre también los resize.
    ScrollTrigger.addEventListener('refreshInit', updateMediaOverflow);

    var mm = gsap.matchMedia();
    instances.set(sectionId, { mm: mm, onRefreshInit: updateMediaOverflow });

    /* --- Movimiento reducido: estado final, sin parallax ni animaciones ---
       El texto visible y la imagen nítida ya los resuelve el CSS; aquí solo
       hay que asegurarse de que ScrollSmoother no mueva el fondo.
       Sin cleanup a propósito: ver la nota de los contextos de velocidad. */
    mm.add(MOTION_REDUCED, function () {
      currentSpeed = 1;
      updateMediaOverflow();
      if (media) applyParallaxSpeed(media, null);
    });

    /* --- Reveal del texto + CTA, y desenfoque si está activado ---
       Mismo comportamiento en cualquier tamaño: no hace falta separar
       breakpoints para esto. */
    mm.add(MOTION_OK, function () {
      var revealTween = null;
      var blurTween = null;

      if (content) {
        revealTween = gsap.fromTo(
          content,
          { autoAlpha: 0, y: 40 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
            overwrite: 'auto',
            scrollTrigger: {
              id: 'brand-teaser-reveal-' + sectionId,
              trigger: root,
              start: 'top 80%',
              once: true, // se dispara una vez, sin scrub
            },
          }
        );
      }

      /* Desenfoque progresivo: ScrollTrigger propio, con scrub, e independiente
         del reveal. Con el checkbox desactivado (o 0px) no se crea nada, ni un
         ScrollTrigger vacío. GSAP anima `filter` frame a frame — el CSS no
         tiene transition en esta propiedad a propósito, o el scrub iría a
         remolque del scroll. */
      if (blurEnabled && blurAmount > 0 && image) {
        root.classList.add('brand-teaser--blur-active'); // will-change: filter

        blurTween = gsap.fromTo(
          image,
          { filter: 'blur(' + blurAmount + 'px)' },
          {
            filter: 'blur(0px)',
            ease: 'none',
            scrollTrigger: {
              id: 'brand-teaser-blur-' + sectionId,
              trigger: root,
              start: 'top bottom', // la sección entra en viewport
              end: function () {
                return '+=' + Math.round(window.innerHeight * (blurScrollVh / 100));
              },
              scrub: true,
              invalidateOnRefresh: true,
            },
          }
        );
      }

      return function () {
        root.classList.remove('brand-teaser--blur-active');
        [revealTween, blurTween].forEach(function (tween) {
          if (!tween) return;
          if (tween.scrollTrigger) tween.scrollTrigger.kill(true);
          tween.kill();
        });
      };
    });

    /* --- Velocidad del parallax por breakpoint ---
       En móvil se amortigua hacia 1: en pantallas altas y estrechas el mismo
       desvío se nota mucho más (el recorrido vertical relativo es mayor) y el
       fondo parece "flotar" respecto al contenido.

       Ninguno de los tres contextos que tocan la velocidad tiene cleanup: los
       tres cubren todos los casos, así que al salir de uno siempre entra otro
       que fija el valor correcto. Restaurando en el cleanup dependeríamos del
       orden entre eventos de dos media queries distintas, que no está
       garantizado, y un salto escritorio -> móvil podría quedarse con la
       velocidad de escritorio. */
    if (media) {
      mm.add(DESKTOP + ' and ' + MOTION_OK, function () {
        currentSpeed = desktopSpeed;
        updateMediaOverflow();
        applyParallaxSpeed(media, desktopSpeed);
        refreshSoon(ScrollTrigger);
      });

      mm.add(MOBILE + ' and ' + MOTION_OK, function () {
        currentSpeed = mobileSpeed;
        updateMediaOverflow();
        applyParallaxSpeed(media, mobileSpeed);
        refreshSoon(ScrollTrigger);
      });
    }

    refreshOnMedia(root, ScrollTrigger);
    refreshSoon(ScrollTrigger);
  }

  /* ---------------------------------------------------------
     Arranque + eventos del Theme Editor
     --------------------------------------------------------- */

  function initAll() {
    document.querySelectorAll('.brand-teaser[data-section-id]').forEach(init);
  }

  window.DermxBrandTeaser = { init: init, initAll: initAll, destroy: destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', function () {
    if (window.gsap && isPluginRegistered('ScrollTrigger')) {
      refreshSoon(window.gsap.core.globals().ScrollTrigger);
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.brand-teaser[data-section-id]');
    if (!root) return;
    init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('.brand-teaser[data-section-id]');
    if (!root) return;
    destroy(root.dataset.sectionId);
  });

  // Al seleccionar/deseleccionar en el editor cambian offsets y alturas.
  ['shopify:section:select', 'shopify:section:deselect', 'shopify:block:select'].forEach(function (
    eventName
  ) {
    document.addEventListener(eventName, function () {
      if (window.gsap && isPluginRegistered('ScrollTrigger')) {
        refreshSoon(window.gsap.core.globals().ScrollTrigger);
      }
    });
  });
})();
